"use client";

import { RefObject, useEffect, useRef } from "react";

export interface UseClickOutsideOptions {
  /** 是否启用外部点击监听 */
  enabled?: boolean;
  
  /** 额外的排除元素引用数组 */
  excludeRefs?: RefObject<HTMLElement>[];
  
  /** 是否监听 ESC 键 */
  listenEscape?: boolean;
}

/**
 * 检测元素外部点击的通用 Hook
 * @param ref 需要检测的元素引用
 * @param callback 外部点击时的回调函数
 * @param options 配置选项
 */
export const useClickOutside = <T extends HTMLElement = HTMLElement>(
  ref: RefObject<T>,
  callback: (event: MouseEvent | KeyboardEvent) => void,
  options: UseClickOutsideOptions = {},
) => {
  const {
    enabled = true,
    excludeRefs = [],
    listenEscape = true,
  } = options;

  const savedCallback = useRef(callback);

  // 更新回调函数引用
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) {return;}

    const handleClickOutside = (event: MouseEvent) => {
      // 检查主要元素
      if (ref.current && ref.current.contains(event.target as Node)) {
        return;
      }

      // 检查排除的元素
      for (const excludeRef of excludeRefs) {
        if (excludeRef.current && excludeRef.current.contains(event.target as Node)) {
          return;
        }
      }

      // 如果点击在所有元素外部，触发回调
      savedCallback.current(event);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (listenEscape && event.key === "Escape") {
        savedCallback.current(event);
      }
    };

    // 使用 mousedown 而不是 click 以确保更快的响应
    document.addEventListener("mousedown", handleClickOutside);
    
    if (listenEscape) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (listenEscape) {
        document.removeEventListener("keydown", handleKeyDown);
      }
    };
  }, [ref, excludeRefs, enabled, listenEscape]);
};